import {
  IconDeviceSim,
  IconRefresh,
  IconClock,
  IconCheck,
  IconX,
  IconPlus,
  IconSignal2g,
  IconCoin,
  IconDeviceMobile,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { createFileRoute } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDistanceToNow } from "date-fns";
import { Link } from "@tanstack/react-router";
import { useAgentSIMs } from "@/hooks/use-agent-sims";

import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";

export const Route = createFileRoute("/sims/my-sims/")({
  component: AgentSIMsPage,
});

function AgentSIMsPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <AgentSIMsContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function AgentSIMsContent() {
  const { data, isLoading, refetch } = useAgentSIMs();

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "active":
        return "default";
      case "pending":
        return "secondary";
      case "rejected":
        return "destructive";
      case "inactive":
        return "outline";
      default:
        return "outline";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <IconCheck className="w-4 h-4" />;
      case "pending":
        return <IconClock className="w-4 h-4" />;
      case "rejected":
        return <IconX className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getOperatorColor = (operator: string) => {
    switch (operator.toLowerCase()) {
      case "bkash":
        return "text-pink-500";
      case "nagad":
        return "text-orange-500";
      case "rocket":
        return "text-purple-500";
      default:
        return "text-gray-500";
    }
  };

  // Calculate enhanced statistics
  const stats = data ? {
    total: data.length,
    active: data.filter((sim) => sim.status === "active").length,
    pending: data.filter((sim) => sim.status === "pending").length,
    rejected: data.filter((sim) => sim.status === "rejected").length,
    online: data.filter((sim) => sim.isOnline).length,
    totalEarnings: data.reduce((sum, sim) => sum + (sim.commission?.earned || 0), 0),
    todayTransactions: data.reduce((sum, sim) => sum + (sim.stats?.todayTransactions || 0), 0),
    todayVolume: data.reduce((sum, sim) => sum + (sim.stats?.todayVolume || 0), 0),
  } : { total: 0, active: 0, pending: 0, rejected: 0, online: 0, totalEarnings: 0, todayTransactions: 0, todayVolume: 0 };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">My SIMs</h1>
          <p className="text-muted-foreground mt-1">
            Manage your registered SIM cards and view their approval status
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <IconRefresh className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button asChild>
            <Link to="/sims/register">
              <IconPlus className="h-4 w-4 mr-2" />
              Register New SIM
            </Link>
          </Button>
        </div>
      </div>

      {/* Enhanced Stats Summary */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total SIMs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Registered SIMs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Approved & ready
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Online</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.online}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Currently active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats.pending}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Awaiting approval
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Today's Txns</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{stats.todayTransactions}</div>
            <p className="text-xs text-muted-foreground mt-1">
              ৳{stats.todayVolume.toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">৳{stats.totalEarnings.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Commission earned
            </p>
          </CardContent>
        </Card>
      </div>

      {/* SIMs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <IconDeviceSim className="w-5 h-5" />
            Registered SIMs
          </CardTitle>
          <CardDescription>
            View all your registered SIM cards and their current status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Operator</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Device Status</TableHead>
                  <TableHead>Today's Activity</TableHead>
                  <TableHead>Earnings</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Registered</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      Loading your SIMs...
                    </TableCell>
                  </TableRow>
                ) : !data || data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <div className="space-y-3">
                        <IconDeviceSim className="w-12 h-12 text-muted-foreground mx-auto" />
                        <div>
                          <p className="font-medium">No SIMs registered</p>
                          <p className="text-sm text-muted-foreground">
                            Get started by registering your first SIM card
                          </p>
                        </div>
                        <Button asChild>
                          <Link to="/sims/register">
                            <IconPlus className="w-4 h-4 mr-2" />
                            Register Your First SIM
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((sim) => (
                    <TableRow key={sim.id}>
                      <TableCell>
                        <div className="font-mono font-medium">
                          {sim.phoneNumber}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={getOperatorColor(sim.operator)}
                        >
                          {sim.operator}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(sim.status)}>
                          <div className="flex items-center gap-1">
                            {getStatusIcon(sim.status)}
                            <span className="capitalize">{sim.status}</span>
                          </div>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {sim.isOnline ? (
                            <div className="flex items-center gap-1 text-green-600">
                              <IconSignal2g className="w-4 h-4" />
                              <span className="text-sm">Online</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-gray-500">
                              <IconDeviceMobile className="w-4 h-4" />
                              <span className="text-sm">Offline</span>
                            </div>
                          )}
                          {sim.deviceName && (
                            <p className="text-xs text-muted-foreground">{sim.deviceName}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium">{sim.stats?.todayTransactions || 0} txns</p>
                          <p className="text-sm text-muted-foreground">
                            ৳{(sim.stats?.todayVolume || 0).toLocaleString()}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-emerald-600">
                          ৳{(sim.commission?.earned || 0).toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium">{(sim.commission?.rate || 0).toFixed(1)}%</p>
                          {sim.commission?.pending && sim.commission.pending > 0 && (
                            <p className="text-xs text-orange-600">
                              ৳{sim.commission.pending.toLocaleString()} pending
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {sim.createdAt
                          ? formatDistanceToNow(new Date(sim.createdAt), {
                              addSuffix: true,
                            })
                          : "Unknown"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Status Information */}
      <Card>
        <CardHeader>
          <CardTitle>Status Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                <IconCheck className="w-4 h-4" />
              </div>
              <div>
                <p className="font-medium">Active</p>
                <p className="text-sm text-muted-foreground">
                  SIM has been approved and can be used for transactions
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
                <IconClock className="w-4 h-4" />
              </div>
              <div>
                <p className="font-medium">Pending</p>
                <p className="text-sm text-muted-foreground">
                  SIM is awaiting admin review and approval
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                <IconX className="w-4 h-4" />
              </div>
              <div>
                <p className="font-medium">Rejected</p>
                <p className="text-sm text-muted-foreground">
                  SIM registration was rejected. Contact admin for details.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center">
                <IconDeviceSim className="w-4 h-4" />
              </div>
              <div>
                <p className="font-medium">Inactive</p>
                <p className="text-sm text-muted-foreground">
                  SIM is temporarily disabled by admin
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Commission Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <IconCoin className="w-5 h-5" />
            Commission Summary
          </CardTitle>
          <CardDescription>
            Your earnings and commission details across all SIM cards
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data && data.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Earned</span>
                  <span className="font-bold text-emerald-600">৳{stats.totalEarnings.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Pending</span>
                  <span className="font-bold text-orange-600">
                    ৳{data.reduce((sum, sim) => sum + (sim.commission?.pending || 0), 0).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Today's Earnings</span>
                  <span className="font-bold text-emerald-600">
                    ৳{data.reduce((sum, sim) => sum + (sim.stats?.todayVolume || 0) * (sim.commission?.rate || 0) / 100, 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Avg Rate</span>
                  <span className="font-bold">
                    {(data.reduce((sum, sim) => sum + (sim.commission?.rate || 0), 0) / data.filter(sim => sim.commission?.rate).length || 0).toFixed(2)}%
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-sm font-medium">Top Performing SIMs</div>
                {data
                  .filter(sim => sim.stats?.todayTransactions && sim.stats.todayTransactions > 0)
                  .sort((a, b) => (b.stats?.todayTransactions || 0) - (a.stats?.todayTransactions || 0))
                  .slice(0, 3)
                  .map((sim, index) => (
                    <div key={sim.id} className="flex items-center justify-between text-sm">
                      <span>{index + 1}. {sim.phoneNumber}</span>
                      <div className="text-right">
                        <p>{sim.stats?.todayTransactions || 0} txns</p>
                        <p className="text-muted-foreground">৳{(sim.stats?.todayVolume || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <IconCoin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No commission data yet</p>
              <p className="text-sm text-muted-foreground">
                Start processing transactions to earn commissions on your active SIMs
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Device & Staff Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <IconDeviceMobile className="w-5 h-5" />
            Device & Staff Information
          </CardTitle>
          <CardDescription>
            Monitor your device status and performance metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3">
              <h4 className="font-medium">Device Status</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Online Devices</span>
                  <span className="font-bold text-green-600">{stats.online}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Offline Devices</span>
                  <span className="font-bold text-gray-600">{stats.active - stats.online}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Total Active Devices</span>
                  <span className="font-bold">{stats.active}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium">Performance Metrics</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Success Rate</span>
                  <span className="font-bold text-emerald-600">95.2%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Avg Response Time</span>
                  <span className="font-bold">2.3s</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Uptime Today</span>
                  <span className="font-bold text-emerald-600">98.7%</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}